import React, { useState, useEffect, useMemo, memo } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import { useLists } from '../contexts/ListsContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useModalClose } from '../hooks/useModalClose';
import '../styles/ImportPlacesModal.css';

const ImportPlacesModal = memo(function ImportPlacesModal({
  isOpen,
  onClose,
  destinationListId,
  destinationListTitle,
  onImportComplete,
}) {
  const { lists, collaborationLists } = useLists();
  const { isClosing, handleClose } = useModalClose(onClose);

  // State
  const [step, setStep] = useState('list'); // 'list' or 'places'
  const [selectedSourceList, setSelectedSourceList] = useState(null);
  const [sourcePlaces, setSourcePlaces] = useState([]);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState(new Set());
  const [existingPlaceIds, setExistingPlaceIds] = useState(new Set());
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  // Handle escape key
  useEscapeKey(isOpen, handleClose);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('list');
      setSelectedSourceList(null);
      setSourcePlaces([]);
      setSelectedPlaceIds(new Set());
      setExistingPlaceIds(new Set());
      setError(null);
    }
  }, [isOpen]);

  // Filter available source lists (exclude current destination list)
  const availableSourceLists = useMemo(() => {
    const allLists = [...lists, ...collaborationLists];
    return allLists.filter(
      (list) =>
        list.id !== destinationListId &&
        (list.items?.length > 0 || list.itemCount > 0)
    );
  }, [lists, collaborationLists, destinationListId]);

  // Fetch source list places when selected
  const handleSelectSourceList = async (sourceList) => {
    setSelectedSourceList(sourceList);
    setLoadingPlaces(true);
    setError(null);

    try {
      // Fetch both source and destination list with full place data
      const [sourceResponse, destResponse] = await Promise.all([
        api.getList(sourceList.id),
        api.getList(destinationListId),
      ]);

      const sourceItems = sourceResponse.data?.items || [];
      const destItems = destResponse.data?.items || [];

      // Track which places already exist in destination
      const existingIds = new Set(destItems.map((item) => item.placeId).filter(Boolean));
      setExistingPlaceIds(existingIds);

      // Deduplicate source items by placeId (keep first occurrence)
      const seenPlaceIds = new Set();
      const uniqueSourceItems = sourceItems.filter((item) => {
        if (!item.placeId || seenPlaceIds.has(item.placeId)) return false;
        seenPlaceIds.add(item.placeId);
        return true;
      });
      setSourcePlaces(uniqueSourceItems);

      // Select all places that don't already exist by default
      const selectableIds = new Set(
        uniqueSourceItems
          .filter((item) => !existingIds.has(item.placeId))
          .map((item) => item.placeId)
      );
      setSelectedPlaceIds(selectableIds);

      setStep('places');
    } catch (err) {
      console.error('Failed to fetch list details:', err);
      setError('Failed to load places. Please try again.');
    } finally {
      setLoadingPlaces(false);
    }
  };

  // Toggle place selection
  const togglePlaceSelection = (placeId) => {
    if (existingPlaceIds.has(placeId)) return; // Can't toggle already-existing places

    setSelectedPlaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) {
        next.delete(placeId);
      } else {
        next.add(placeId);
      }
      return next;
    });
  };

  // Select/deselect all
  const handleSelectAll = () => {
    const selectableIds = sourcePlaces
      .filter((item) => !existingPlaceIds.has(item.placeId))
      .map((item) => item.placeId);
    setSelectedPlaceIds(new Set(selectableIds));
  };

  const handleDeselectAll = () => {
    setSelectedPlaceIds(new Set());
  };

  // Calculate counts
  const selectableCount = sourcePlaces.filter(
    (item) => !existingPlaceIds.has(item.placeId)
  ).length;
  const alreadyInListCount = sourcePlaces.length - selectableCount;
  const selectedCount = selectedPlaceIds.size;

  // Handle import
  const handleImport = async () => {
    if (selectedPlaceIds.size === 0) return;

    setImporting(true);
    setError(null);

    try {
      const response = await api.importPlaces(
        destinationListId,
        selectedSourceList.id,
        [...selectedPlaceIds]
      );

      if (response.success) {
        onImportComplete?.(response.data);
        handleClose();
      } else {
        setError(response.error || 'Failed to import places');
      }
    } catch (err) {
      console.error('Failed to import places:', err);
      setError(err.message || 'Failed to import places. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  // Go back to list selection
  const handleBack = () => {
    setStep('list');
    setSelectedSourceList(null);
    setSourcePlaces([]);
    setSelectedPlaceIds(new Set());
    setExistingPlaceIds(new Set());
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="import-modal-overlay" onClick={handleClose}>
      <div
        className={`import-modal${isClosing ? ' closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="import-modal-header">
          {step === 'places' && (
            <button
              className="import-modal-back"
              onClick={handleBack}
              aria-label="Go back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <h2>
            <span className="import-modal-title-text">
              {step === 'list' ? 'Import Places' : `From ${selectedSourceList?.title || selectedSourceList?.name}`}
            </span>
          </h2>
          <button
            className="import-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="import-modal-content">
          {step === 'list' && (
            <>
              <p className="import-modal-subtitle">
                Select a list to import places from
              </p>
              {availableSourceLists.length === 0 ? (
                <div className="import-empty-state">
                  <p>No other lists with places available</p>
                </div>
              ) : (
                <div className="import-list-picker">
                  {availableSourceLists.map((list) => (
                    <button
                      key={list.id}
                      className="import-list-option"
                      onClick={() => handleSelectSourceList(list)}
                      disabled={loadingPlaces}
                    >
                      <div className="import-list-option-info">
                        <span className="import-list-option-title">
                          {list.title || list.name}
                        </span>
                        <span className="import-list-option-count">
                          {list.items?.length || list.itemCount || 0} places
                        </span>
                      </div>
                      <svg
                        className="import-list-option-arrow"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 'places' && (
            <>
              {loadingPlaces ? (
                <div className="import-loading">
                  <div className="loading-spinner" />
                </div>
              ) : (
                <>
                  <div className="import-bulk-actions">
                    <button
                      className="import-bulk-btn"
                      onClick={handleSelectAll}
                      disabled={selectedCount === selectableCount}
                    >
                      Select All
                    </button>
                    <button
                      className="import-bulk-btn"
                      onClick={handleDeselectAll}
                      disabled={selectedCount === 0}
                    >
                      Deselect All
                    </button>
                  </div>

                  {alreadyInListCount > 0 && (
                    <div className="import-info-banner">
                      {alreadyInListCount} place{alreadyInListCount !== 1 ? 's' : ''} already in list (will be skipped)
                    </div>
                  )}

                  <div className="import-places-list">
                    {sourcePlaces.map((place) => {
                      const isExisting = existingPlaceIds.has(place.placeId);
                      const isSelected = selectedPlaceIds.has(place.placeId);

                      return (
                        <label
                          key={place.placeId}
                          className={`import-place-item${isExisting ? ' disabled' : ''}${isSelected ? ' selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePlaceSelection(place.placeId)}
                            disabled={isExisting}
                          />
                          <div className="import-place-photo">
                            {place.photoUrl ? (
                              <img src={place.photoUrl} alt="" />
                            ) : (
                              <div className="import-place-photo-placeholder">
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="import-place-info">
                            <span className="import-place-name">{place.name}</span>
                            {place.address && (
                              <span className="import-place-address">
                                {place.address.split(',')[0]}
                              </span>
                            )}
                          </div>
                          {isExisting && (
                            <span className="import-place-existing-badge">
                              Already added
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {error && <div className="import-error">{error}</div>}
        </div>

        {step === 'places' && !loadingPlaces && (
          <div className="import-modal-footer">
            <div className="import-footer-info">
              {selectedCount === selectableCount
                ? `All ${selectableCount} new place${selectableCount !== 1 ? 's' : ''} selected`
                : `${selectedCount} of ${selectableCount} new places selected`}
            </div>
            <button
              className="import-submit-btn"
              onClick={handleImport}
              disabled={selectedCount === 0 || importing}
            >
              {importing ? 'Importing...' : `Import ${selectedCount} Place${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
});

export default ImportPlacesModal;
